import React, { createContext, useContext, useState, useCallback } from 'react';

// Create the context
const NotificationContext = createContext(null);

/**
 * NotificationProvider - Context provider for application notifications
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components
 */
export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);

  // Add a new notification
  const addNotification = useCallback((notification) => {
    const id = Date.now().toString();
    
    // Create a complete notification object with defaults
    const completeNotification = {
      id,
      message: notification.message || 'Notification',
      severity: notification.severity || 'info',
      duration: notification.duration || 3500,
      position: notification.position || { vertical: 'top', horizontal: 'center' },
      ...notification
    };
    
    setNotifications(prev => [...prev, completeNotification]);
    
    // Auto-remove notification after duration
    if (completeNotification.duration) {
      setTimeout(() => {
        removeNotification(id);
      }, completeNotification.duration);
    }
    
    return id;
  }, []);

  // Remove a notification by ID
  const removeNotification = useCallback((id) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  }, []);

  // Clear all notifications
  const clearNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  // Create shorthand methods for different severity levels
  const notificationSuccess = useCallback((message, options = {}) => {
    return addNotification({ message, severity: 'success', ...options });
  }, [addNotification]);

  const notificationError = useCallback((message, options = {}) => {
    return addNotification({ message, severity: 'error', ...options });
  }, [addNotification]);

  const notificationInfo = useCallback((message, options = {}) => {
    return addNotification({ message, severity: 'info', ...options });
  }, [addNotification]);

  const notificationWarning = useCallback((message, options = {}) => {
    return addNotification({ message, severity: 'warning', ...options });
  }, [addNotification]);

  const value = {
    notifications,
    addNotification,
    removeNotification,
    clearNotifications,
    notificationSuccess,
    notificationError,
    notificationInfo,
    notificationWarning
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

// Create a custom hook to use the notification context
export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};